import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useKidsStore from '../store/kidsStore';
import { mediaSrc } from '../api/media';
import InfoBanner from '../components/InfoBanner';

const ParentLearn: React.FC = () => {
  const navigate = useNavigate();
  const { kids, fetchKids } = useKidsStore();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '', grade: 1 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchKids(); }, [fetchKids]);

  const handleAddKid = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      await api.post('/api/parent/kids', form);
      setForm({ name: '', username: '', password: '', grade: 1 });
      setShowAdd(false);
      fetchKids();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to add kid');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="kt-card p-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-display font-extrabold text-gray-800">KidsTrack 🚀</h1>
          <p className="text-sm text-gray-500">Pick a kid to manage their learning.</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="bg-brand-blue text-white text-sm font-bold px-3 py-2 rounded-full shrink-0">+ Add Kid</button>
      </div>

      {kids.length === 0 ? (
        <InfoBanner
          tone="blue"
          icon="🧒"
          title="No kids yet — add your first child"
          steps={[
            'Tap the “+ Add Kid” button above.',
            'Enter a name, username, password and grade.',
            'The username & password you set are your child’s login — share them so they can sign in.',
          ]}
          cta={{ label: '+ Add Kid', onClick: () => setShowAdd(true) }}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {kids.map((k) => (
            <button key={k.id} onClick={() => navigate(`/parent/kids/${k.id}/learn`)}
              className="kt-card p-4 flex items-center gap-3 hover:shadow-md transition active:scale-[0.99] text-left">
              {k.profilePhotoUrl ? (
                <img src={mediaSrc(k.profilePhotoUrl)} alt={k.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-brand-orange/30" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">🧒</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-gray-800 truncate">{k.name}</p>
                <p className="text-xs text-gray-500">Grade {k.grade} · @{k.username}</p>
              </div>
              <span className="text-gray-300 text-xl">›</span>
            </button>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-gray-800 mb-3">Add New Kid</h3>
            <form onSubmit={handleAddKid} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Name</label>
                  <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" placeholder="Kid's name" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Username</label>
                  <input type="text" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" placeholder="Login username" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Password</label>
                  <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" placeholder="Login password" />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Grade</label>
                  <select value={form.grade} onChange={(e) => setForm({ ...form, grade: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
                    {[1,2,3,4,5,6,7,8,9,10].map((g) => <option key={g} value={g}>Grade {g}</option>)}
                  </select>
                </div>
              </div>
              {formError && <p className="text-red-500 text-sm">{formError}</p>}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button type="submit" disabled={saving} className="bg-brand-blue text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Kid'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentLearn;
